import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { MyProjectComponent } from '../../shared/components/my-project/my-project.component';
import { ModalComponent } from '../../shared/components/primeng/modal/modal.component';
import { CanComponentDeactivate } from '../../guards/my-projects-deactivate.guard';
import { AuthApiService } from '@angular-workspace/services';
import { Observable } from 'rxjs';

/** Access to My Projects: only allowed when Compute is in active roles (not just pending). */
export type MyProjectsAccessLevel = 'loading' | 'none' | 'pending' | 'full';

@Component({
  selector: 'app-org-admin-my-projects',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    MyProjectComponent,
    ModalComponent,
  ],
  templateUrl: './my-projects.component.html',
  styleUrl: './my-projects.component.scss',
})
export class OrgAdminMyProjectsComponent implements CanComponentDeactivate {
  @ViewChild(MyProjectComponent) myProjectComponent!: MyProjectComponent;

  accessLevel: MyProjectsAccessLevel = 'loading';
  accessModalDismissed = false;

  get showAccessModal(): boolean {
    return (this.accessLevel === 'none' || this.accessLevel === 'pending') && !this.accessModalDismissed;
  }

  constructor(
    private authApiService: AuthApiService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.checkComputeAccess();
  }

  private async checkComputeAccess(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      const actual = userData?.['result'] ?? userData;
      const roles: string[] = actual?.roles ?? [];
      const pendingRoles: string[] = actual?.pendingRoles ?? [];

      const hasActiveCompute = roles.some((r: string) => r.toLowerCase() === 'compute');
      const hasPendingCompute = pendingRoles.some((r: string) => r.toLowerCase() === 'compute');

      if (hasActiveCompute) {
        this.accessLevel = 'full';
      } else if (hasPendingCompute) {
        this.accessLevel = 'pending';
      } else {
        this.accessLevel = 'none';
      }
    } catch (error) {
      console.error('Error checking Compute role for My Projects:', error);
      this.accessLevel = 'none';
    }
  }

  onAccessModalClose(visible: boolean): void {
    if (visible) {
      return;
    }
    this.router.navigate(['/org-admin/home']);
  }

  onRoleUpgradeClick(): void {
    this.accessModalDismissed = true;
    window.dispatchEvent(new CustomEvent('openUpgradeModal', { detail: { source: 'my-projects' } }));
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.myProjectComponent) {
      return this.myProjectComponent.canDeactivate();
    }
    return true;
  }
}
